import React from "react";

import { ExperimentDetailsForm } from "./ExperimentDetailsForm";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useExperimentEditor } from "../../hooks/useExperimentEditor";
import { useExperimentValidation } from "../../hooks/useExperimentValidation";
import { formatExperimentStatusLabel } from "../../utils/experimentStatus";
import { validateContextStep } from "../../validations/contextValidation";

import type { ExperimentFormData } from "../../types/experiment.types";

declare global {
  interface Window {
    validateCurrentStep?: () => { isValid: boolean; missingFields: string[] };
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ExperimentDetailsHeader: React.FC = () => (
  <div className="mb-8">
    <div>
      <h2 className="mb-2 flex items-center text-xl font-semibold text-white">
        <div className="mr-3 h-6 w-2 rounded-full bg-amber-400" />
        Experiment Details
      </h2>
      <p className="text-sm text-white/60">
        Provide the core details and strategic context that define your
        experiment
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

interface ExperimentDetailsSectionProps {
  experimentData: ExperimentFormData;
  setExperimentData: (updater: (prev: ExperimentFormData) => ExperimentFormData) => void;
  resetTrigger?: number;
  onFieldHelp?: (fieldType: string) => void;
  isCommentMode?: boolean;
}

export const ExperimentDetailsSection: React.FC<ExperimentDetailsSectionProps> = ({
  experimentData,
  setExperimentData,
  onFieldHelp,
  isCommentMode = false,
}) => {
  const { experimentStatus, isExperimentViewOnly } = useExperimentEditor();
  const { errors, validateField, getFieldStatus, completionPercentage } =
    useExperimentValidation(experimentData);

  useAutoSave({
    data: experimentData,
    onSave: async () => {
      await new Promise((resolve) => setTimeout(resolve, 300));
    },
    debounceMs: 60000,
  });

  const getNextRecommendedFields = (): string[] => {
    const fields: string[] = [];
    if (!experimentData.name?.trim()) fields.push("experiment name");
    if (!experimentData.goal?.trim()) fields.push("business goal");
    if (!experimentData.description?.trim()) fields.push("context & background");
    return fields;
  };

  const nextRecommendedFields = getNextRecommendedFields();
  const contextValidation = React.useMemo(
    () =>
      validateContextStep({
        name: experimentData.name,
        goal: experimentData.goal,
        description: experimentData.description,
      }),
    [experimentData.description, experimentData.goal, experimentData.name],
  );

  React.useEffect(() => {
    window.validateCurrentStep = () => ({
      isValid: contextValidation.isValid,
      missingFields: contextValidation.missingFields,
    });

    return () => {
      window.validateCurrentStep = undefined;
    };
  }, [contextValidation]);

  return (
    <div className="flex flex-col px-0 py-4 sm:py-6 text-white">
        <div className="mx-auto flex w-full max-w-7xl flex-1">
          <div className="w-full">
            <ExperimentDetailsHeader />

            {isExperimentViewOnly ? (
              <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                This experiment is{" "}
                {formatExperimentStatusLabel(experimentStatus).toLowerCase()} and
                can only be viewed.
              </div>
            ) : null}

            <div className={isExperimentViewOnly ? "pointer-events-none opacity-70" : ""}>
              <ExperimentDetailsForm
                experimentData={experimentData}
                setExperimentData={setExperimentData}
                errors={errors}
                validateField={validateField}
                getFieldStatus={getFieldStatus}
                completionPercentage={completionPercentage}
                nextRecommendedFields={nextRecommendedFields}
                onFieldHelp={onFieldHelp}
                isCommentMode={isCommentMode}
                hideHeader
              />
            </div>
          </div>
        </div>
      </div>
  );
};
